import { Head, Link, router } from "@inertiajs/react";
import {
  ArrowLeft,
  Building2,
  FileText,
  Lock,
  Trash2,
  Unlock,
  Upload,
  Users,
  Wrench,
} from "lucide-react";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout.tsx";
import {
  bizminer_admin_organization_account_path,
  bizminer_admin_organization_account_pillar_insight_path,
  upload_bizminer_admin_organization_account_pillar_insight_path,
} from "@/routes";
import type {
  BizminerAdminAccount,
  BizminerAdminOrganizationList,
  BizminerPillarInsight,
} from "@/types/serializers";
import PageHeader from "../Organizations/_components/PageHeader";
import DocxUploadDialog from "./_components/DocxUploadDialog";

interface Props {
  pillar_insight: BizminerPillarInsight;
  account: BizminerAdminAccount;
  organization: BizminerAdminOrganizationList;
}

function StatusBadge({ unlocked }: { unlocked: boolean }) {
  if (unlocked) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-chart-6/10 text-chart-6 text-sm">
        <Unlock className="h-3.5 w-3.5" />
        Unlocked
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-muted text-muted-foreground text-sm">
      <Lock className="h-3.5 w-3.5" />
      Locked
    </span>
  );
}

function OverviewCard({
  pillarInsight,
}: {
  pillarInsight: BizminerPillarInsight;
}) {
  return (
    <div className="bg-card shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-card-foreground mb-4">
        Overview
      </h3>
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Title</dt>
            <dd className="mt-1 text-sm text-card-foreground">
              {pillarInsight.title}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Slug</dt>
            <dd className="mt-1 text-sm text-card-foreground font-mono">
              {pillarInsight.slug}
            </dd>
          </div>
        </div>
        <div>
          <dt className="text-sm font-medium text-muted-foreground">
            Description
          </dt>
          <dd className="mt-1 text-sm text-card-foreground">
            {pillarInsight.description || (
              <span className="italic text-muted-foreground">
                No description
              </span>
            )}
          </dd>
        </div>
        <div className="flex items-center gap-4">
          <div>
            <dt className="text-sm font-medium text-muted-foreground">Icon</dt>
            <dd className="mt-1 text-sm text-card-foreground font-mono">
              {pillarInsight.icon || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-muted-foreground">
              Status
            </dt>
            <dd className="mt-1">
              <StatusBadge unlocked={pillarInsight.unlocked} />
            </dd>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExecutiveSummaryCard({ summary }: { summary: string }) {
  return (
    <div className="bg-card shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-card-foreground mb-4">
        Executive Summary
      </h3>
      <p className="text-sm text-card-foreground whitespace-pre-wrap">
        {summary}
      </p>
    </div>
  );
}

function CompetitorsList({
  competitors,
}: {
  competitors: BizminerPillarInsight["competitors"];
}) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Users className="h-5 w-5 text-muted-foreground" />
        <h4 className="text-sm font-medium text-card-foreground">
          Competitors
        </h4>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
          {competitors.length}
        </span>
      </div>
      {competitors.length > 0 ? (
        <ul className="space-y-1.5">
          {competitors.map((competitor) => (
            <li
              key={competitor.id}
              className="text-sm text-card-foreground pl-3 border-l-2 border-border"
            >
              {competitor.name}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          No competitors defined
        </p>
      )}
    </div>
  );
}

function ImprovementLeversSummary({ count }: { count: number }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Wrench className="h-5 w-5 text-muted-foreground" />
        <h4 className="text-sm font-medium text-card-foreground">
          Improvement Levers
        </h4>
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
          {count}
        </span>
      </div>
      {count > 0 ? (
        <p className="text-sm text-muted-foreground">
          This pillar insight has{" "}
          <span className="font-medium text-card-foreground">{count}</span>{" "}
          improvement lever{count !== 1 ? "s" : ""} defined.
        </p>
      ) : (
        <p className="text-sm text-muted-foreground italic">
          No improvement levers defined
        </p>
      )}
    </div>
  );
}

function DataSummaryCard({
  pillarInsight,
}: {
  pillarInsight: BizminerPillarInsight;
}) {
  return (
    <div className="bg-card shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-card-foreground mb-4">
        Data Summary
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <CompetitorsList competitors={pillarInsight.competitors} />
        <ImprovementLeversSummary
          count={pillarInsight.improvement_lever_count}
        />
      </div>
    </div>
  );
}

function ContextCard({
  account,
  organization,
}: {
  account: BizminerAdminAccount;
  organization: BizminerAdminOrganizationList;
}) {
  return (
    <div className="bg-card shadow rounded-lg p-6">
      <h3 className="text-lg font-medium text-card-foreground mb-4">Context</h3>
      <div className="flex items-start space-x-3">
        <Building2 className="h-5 w-5 text-muted-foreground mt-0.5" />
        <div>
          <Link
            href={bizminer_admin_organization_account_path(
              organization.id,
              account.id,
            )}
            className="text-sm font-medium text-primary hover:text-primary/80"
          >
            {account.name}
          </Link>
          <div className="text-xs text-muted-foreground">
            {organization.name}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeaderActions({
  pillarInsight,
  organization,
  account,
  onReset,
}: {
  pillarInsight: BizminerPillarInsight;
  organization: BizminerAdminOrganizationList;
  account: BizminerAdminAccount;
  onReset: () => void;
}) {
  return (
    <div className="flex gap-2">
      <Link
        href={upload_bizminer_admin_organization_account_pillar_insight_path(
          organization.id,
          account.id,
          pillarInsight.id,
        )}
        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
      >
        <Upload className="h-4 w-4 mr-2" />
        Upload
      </Link>
      <DocxUploadDialog
        pillarInsight={pillarInsight}
        organizationId={organization.id}
        accountId={account.id}
        trigger={
          <button
            type="button"
            className="inline-flex items-center px-4 py-2 border border-input text-sm font-medium rounded-md text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
          >
            <FileText className="h-4 w-4 mr-2" />
            DOCX Report
          </button>
        }
      />
      {pillarInsight.unlocked && (
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring"
        >
          <Trash2 className="h-4 w-4 mr-2" />
          Reset
        </button>
      )}
    </div>
  );
}

export default function Show({ pillar_insight, account, organization }: Props) {
  const handleReset = () => {
    if (
      confirm(
        `Are you sure you want to reset "${pillar_insight.title}"? This will clear all data and improvement levers for this pillar insight.`,
      )
    ) {
      router.delete(
        bizminer_admin_organization_account_pillar_insight_path(
          organization.id,
          account.id,
          pillar_insight.id,
        ),
      );
    }
  };

  return (
    <BizminerAdminLayout>
      <Head title={`${pillar_insight.title} - ${account.name} - Super Admin`} />
      <div className="min-h-screen">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <Link
            href={bizminer_admin_organization_account_path(
              organization.id,
              account.id,
            )}
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to {account.name}
          </Link>
        </div>

        <PageHeader
          title={pillar_insight.title}
          icon={
            pillar_insight.unlocked ? (
              <Unlock className="h-8 w-8 text-chart-6" />
            ) : (
              <Lock className="h-8 w-8 text-muted-foreground" />
            )
          }
          actions={
            <HeaderActions
              pillarInsight={pillar_insight}
              organization={organization}
              account={account}
              onReset={handleReset}
            />
          }
        />

        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0 space-y-6">
            <OverviewCard pillarInsight={pillar_insight} />

            {pillar_insight.unlocked && pillar_insight.executive_summary && (
              <ExecutiveSummaryCard
                summary={pillar_insight.executive_summary}
              />
            )}

            <DataSummaryCard pillarInsight={pillar_insight} />

            <ContextCard account={account} organization={organization} />
          </div>
        </div>
      </div>
    </BizminerAdminLayout>
  );
}
